import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { AxiosError } from 'axios';
import api from '../../services/api';
import { TotemsListParams, TotemsListResponse } from './typesAdmin';

const TOTEMS_ADMIN_QUERY_ROOT = 'GET_TOTEMS_ADMIN' as const;

export const serializeTotemsListParams = (p: TotemsListParams) =>
    JSON.stringify({
        page: p.page,
        perPage: p.perPage,
        totemId: p.totemId ?? null,
        storeId: p.storeId ?? null,
        shopmanagerId: p.shopmanagerId ?? null,
        androidUid: p.androidUid ?? null,
        hardwareModel: p.hardwareModel ?? null,
        nome: p.nome ?? null,
        isAuthenticated:
            p.isAuthenticated === undefined ? null : p.isAuthenticated,
        type: p.type ?? null,
        version: p.version ?? null,
    });

const parseTotemsListParams = (snapshot: string): TotemsListParams => {
    const o = JSON.parse(snapshot) as {
        page: number;
        perPage: number;
        totemId: number | null;
        storeId: number | null;
        shopmanagerId: number | null;
        androidUid: string | null;
        hardwareModel: string | null;
        nome: string | null;
        isAuthenticated: boolean | null;
        type: string | null;
        version: string | null;
    };
    return {
        page: o.page,
        perPage: o.perPage,
        ...(o.totemId != null && { totemId: o.totemId }),
        ...(o.storeId != null && { storeId: o.storeId }),
        ...(o.shopmanagerId != null && { shopmanagerId: o.shopmanagerId }),
        ...(o.androidUid != null &&
            o.androidUid !== '' && {
                androidUid: o.androidUid,
            }),
        ...(o.hardwareModel != null &&
            o.hardwareModel !== '' && {
                hardwareModel: o.hardwareModel,
            }),
        ...(o.nome != null && o.nome !== '' && { nome: o.nome }),
        ...(o.isAuthenticated !== null && {
            isAuthenticated: o.isAuthenticated,
        }),
        ...(o.type != null && o.type !== '' && { type: o.type }),
        ...(o.version != null && o.version !== '' && { version: o.version }),
    };
};

const buildParams = (params: TotemsListParams) => {
    const out: Record<string, string | number | boolean | undefined> = {
        page: params.page,
        perPage: params.perPage,
    };
    if (params.totemId !== undefined && params.totemId !== null)
        out.totemId = params.totemId;
    if (params.storeId !== undefined && params.storeId !== null)
        out.storeId = params.storeId;
    if (params.shopmanagerId !== undefined && params.shopmanagerId !== null)
        out.shopmanagerId = params.shopmanagerId;
    if (params.androidUid?.trim()) out.androidUid = params.androidUid.trim();
    if (params.hardwareModel?.trim())
        out.hardwareModel = params.hardwareModel.trim();
    if (params.nome?.trim()) out.nome = params.nome.trim();
    if (params.isAuthenticated !== undefined && params.isAuthenticated !== null)
        out.isAuthenticated = params.isAuthenticated;
    if (params.type?.trim()) out.type = params.type.trim();
    if (params.version?.trim()) out.version = params.version.trim();
    return out;
};

const fetchTotemsAdmin = async ({ queryKey }: QueryFunctionContext) => {
    const [, snapshot] = queryKey as [typeof TOTEMS_ADMIN_QUERY_ROOT, string];
    const requestParams = parseTotemsListParams(snapshot);
    const { data: body } = await api.get<TotemsListResponse>('/totems', {
        params: buildParams(requestParams),
    });
    return {
        data: body.data,
        meta: body.meta,
    };
};

export function useGetTotemsAdmin(params: TotemsListParams) {
    return useQuery({
        queryKey: [TOTEMS_ADMIN_QUERY_ROOT, serializeTotemsListParams(params)],
        queryFn: fetchTotemsAdmin,
        keepPreviousData: false,
        onError: (error: unknown) => {
            const err = error as AxiosError<{ message?: string }>;
            message.error(
                err.response?.data?.message ??
                    'Não foi possível carregar os totens.',
            );
        },
        refetchOnWindowFocus: false,
    });
}
